import { Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
	BvedCostType,
	CommodityWithType,
	CostGroup,
	CostGroupRequest,
	CostGroupService,
	CostTemplate,
	InvoiceAmount,
	InvoiceAmountType,
	InvoiceCategory,
	InvoiceCategoryType,
	InvoiceDelivery,
	PhysicalUnitWithType,
} from '@eadsysteme/billingclient-v1';
import { stagger40ms } from '../../../../../../../@vex/animations/stagger.animation';
import { fadeInUp400ms } from '../../../../../../../@vex/animations/fade-in-up.animation';
import { BillingMetaData } from '../../../../../../services/data/billingMetaData';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { LayoutService } from '../../../../../../../@vex/services/layout.service';
import { MatCard, MatCardContent, MatCardHeader } from '@angular/material/card';
import { MatList, MatListItem } from '@angular/material/list';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatRow,
	MatRowDef,
	MatTable,
} from '@angular/material/table';
import { MatOption, MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { SessionService } from '../../../../../../services/session.service';
import { CookieService } from 'ngx-cookie-service';
import { SnackbarService } from '../../../../../../services/snackbar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CostAllocationDetail } from '../../../../../../services/data/CostAllocationDetail';
import { CostAllocationHelper } from '../../../../../../services/CostAllocationHelper.service';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { PeriodService } from '../../../period.service';
import { BvedMainCategory, getMainCategoryFromKey, getMainCategoryI18nKey } from '../../../../../../util/models/bvedCostType/BvedMainCategory';
import { BvedCostTypeData } from '../../../../../../util/models/bvedCostType/BvedCostType.data';
import { AllocationOptionComponent } from '../../shared/allocation-option/allocation-option.component';

@Component({
	selector: 'vex-expert-cost-group-dialog',
	imports: [
		MatButton,
		MatDialogActions,
		MatDialogContent,
		MatDialogTitle,
		MatIcon,
		ReactiveFormsModule,
		TranslocoDirective,
		MatFormField,
		MatInput,
		MatLabel,
		MatTooltip,
		MatList,
		MatListItem,
		MatCard,
		MatCardHeader,
		MatCardContent,
		MatTable,
		MatColumnDef,
		MatHeaderCell,
		MatHeaderCellDef,
		MatCellDef,
		MatCell,
		MatIconButton,
		MatHeaderRow,
		MatRow,
		MatRowDef,
		MatHeaderRowDef,
		MatSelect,
		MatOption,
		MatExpansionPanelTitle,
		MatExpansionPanel,
		MatExpansionPanelHeader,
		MatSelectTrigger,
		CdkDrag,
		CdkDragHandle,
		AllocationOptionComponent,
	],
	templateUrl: './expert-cost-group-dialog.component.html',
	styleUrl: './expert-cost-group-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [fadeInUp400ms, stagger40ms],
})
export class ExpertCostGroupDialogComponent implements OnInit {
	private readonly dialogRef = inject(MatDialogRef<ExpertCostGroupDialogComponent>);
	private readonly dialogData = inject(MAT_DIALOG_DATA);
	private readonly formBuilder = inject(FormBuilder);
	private readonly destroyRef = inject(DestroyRef);
	private readonly sessionService = inject(SessionService);
	private readonly cookieService = inject(CookieService);
	protected layoutService = inject(LayoutService);
	private readonly snackBar = inject(SnackbarService);
	private readonly billingMetaData = inject(BillingMetaData);
	private readonly costGroupService = inject(CostGroupService);
	private readonly transloco = inject(TranslocoService);
	protected periodService = inject(PeriodService);
	private readonly costAllocationDetailService = inject(CostAllocationHelper);

	private billingId: string = this.dialogData.billingId;
	private billingPeriodId: string = this.dialogData.billingPeriodId;
	private currentCostGroup: WritableSignal<CostGroup> = signal(undefined);
	private currentTemplate: WritableSignal<CostTemplate> = signal(undefined);

	protected physicalUnits: WritableSignal<PhysicalUnitWithType[]> = signal(undefined);
	protected commodities: WritableSignal<CommodityWithType[]> = signal(undefined);
	protected costGroupFg: FormGroup;
	protected selectableInvoiceCategories = signal<InvoiceCategoryType[]>(Object.values(InvoiceCategoryType));
	protected selectedInvoiceCategories = signal<InvoiceCategoryType[]>([]);
	deliveries = signal<InvoiceDelivery[]>([]);
	protected selectableInvoiceAmounts = signal<InvoiceAmountType[]>(Object.values(InvoiceAmountType));
	protected selectedInvoiceAmounts = signal<InvoiceAmountType[]>([]);
	displayedColumns: string[] = ['commodity', 'unit', 'actions'];
	deliveryForm: FormGroup;
	isEditMode = false;
	editingCommodity: InvoiceDelivery = undefined;
	allocations = signal<CostAllocationDetail[]>(this.dialogData.allocations);
	bvedCostTypes = signal<BvedCostType[]>(this.dialogData.bvedCostTypes);
	protected costTypeMeta = this.dialogData.costTypes;

	ngOnInit(): void {
		this.costGroupFg = this.formBuilder.group({
			commodity: [{ value: null, disabled: this.periodService.selectedPeriod().locked }, Validators.required],
			unit: [{ value: null, disabled: this.periodService.selectedPeriod().locked }, Validators.required],
			name: [{ value: null, disabled: this.periodService.selectedPeriod().locked }, Validators.required],
			printName: [{ value: null, disabled: this.periodService.selectedPeriod().locked }, Validators.required],
			description: [{ value: null, disabled: this.periodService.selectedPeriod().locked }],
			invoiceCategories: [{ value: [], disabled: this.periodService.selectedPeriod().locked }],
			invoiceDeliveries: [{ value: [], disabled: this.periodService.selectedPeriod().locked }],
			selectedAllocation: [{ value: [], disabled: this.periodService.selectedPeriod().locked }],
			selectedBvedCostType: [{ value: [], disabled: this.periodService.selectedPeriod().locked }],
			selectedCostType: [{ value: [], disabled: this.periodService.selectedPeriod().locked }],
		});

		this.deliveryForm = this.formBuilder.group({
			commodity: [{ value: [], disabled: this.periodService.selectedPeriod().locked }, Validators.required],
			physicalUnit: [{ value: [], disabled: this.periodService.selectedPeriod().locked }, Validators.required],
		});

		this.commodities.set(this.billingMetaData.getCommodities());
		this.physicalUnits.set(this.billingMetaData.getUnits());
		this.currentCostGroup.set(this.dialogData.currentCostGroup);
		this.currentTemplate.set(this.dialogData.currentTemplate);

		this.costGroupFg.get('name').setValue(this.currentCostGroup().name);
		this.costGroupFg.get('printName').setValue(this.currentCostGroup().printname);
		this.costGroupFg.get('description').setValue(this.currentCostGroup().description);

		const costAllocationDetail = this.allocations().find((value) => value.exportId === this.currentCostGroup().costAllocationExportId);
		this.costGroupFg.get('selectedAllocation').setValue(costAllocationDetail);
		const bvedCostType = this.bvedCostTypes().find((value) => value === this.currentCostGroup().bvedCostType);
		this.costGroupFg.get('selectedBvedCostType').setValue(bvedCostType);
		this.costGroupFg.get('selectedCostType').setValue(this.currentCostGroup().costType);

		this.fillInvoiceCategories();
		this.fillInvoiceDeliveries();
		this.fillInvoiceAmounts();
	}

	private fillInvoiceCategories() {
		this.selectedInvoiceCategories.set(this.currentCostGroup().invoiceCategories.map((category) => category.type));
		const availableInvoiceCategories = computed(() => {
			const selectable = this.selectableInvoiceCategories();
			const selected = new Set(this.selectedInvoiceCategories());
			return selectable.filter((category) => !selected.has(category));
		});
		this.selectableInvoiceCategories.set(availableInvoiceCategories());
	}

	private fillInvoiceAmounts() {
		this.selectedInvoiceAmounts.set(this.currentCostGroup().invoiceAmounts.map((category) => category.type));
		const availableInvoiceCategories = computed(() => {
			const selectable = this.selectableInvoiceAmounts();
			const selected = new Set(this.selectedInvoiceAmounts());
			return selectable.filter((category) => !selected.has(category));
		});
		this.selectableInvoiceAmounts.set(availableInvoiceCategories());
	}

	moveInvoiceCategory(item: any, fromUpperList: boolean) {
		if (this.periodService.selectedPeriod().locked) {
			return;
		}
		if (fromUpperList) {
			if (!this.selectableInvoiceCategories().includes(item)) {
				this.selectableInvoiceCategories.update((list) => [...list, item]);
			}
			this.selectedInvoiceCategories.update((list) => list.filter((i) => i !== item));
		} else {
			if (!this.selectedInvoiceCategories().includes(item)) {
				this.selectedInvoiceCategories.update((list) => [...list, item]);
			}
			this.selectableInvoiceCategories.update((list) => list.filter((i) => i !== item));
		}
	}

	moveInvoiceAmount(item: any, fromUpperList: boolean) {
		if (this.periodService.selectedPeriod().locked) {
			return;
		}
		if (fromUpperList) {
			if (!this.selectableInvoiceAmounts().includes(item)) {
				this.selectableInvoiceAmounts.update((list) => [...list, item]);
			}
			this.selectedInvoiceAmounts.update((list) => list.filter((i) => i !== item));
		} else {
			if (!this.selectedInvoiceAmounts().includes(item)) {
				this.selectedInvoiceAmounts.update((list) => [...list, item]);
			}
			this.selectableInvoiceAmounts.update((list) => list.filter((i) => i !== item));
		}
	}

	close(toSave: boolean) {
		if (!toSave) {
			this.dialogRef.close({ saved: false }); //close this dialog, but not the costGroupDialog
			return;
		}
		const invoiceAmounts = this.selectedInvoiceAmounts().map((value) => {
			const invoiceAmount: InvoiceAmount = {
				type: value,
			};
			return invoiceAmount;
		});
		const invoiceCategories = this.selectedInvoiceCategories().map((value) => {
			const invoiceCategory: InvoiceCategory = {
				type: value,
			};
			return invoiceCategory;
		});

		const syncedCategories: InvoiceCategory[] = this.syncLists(this.currentCostGroup().invoiceCategories, invoiceCategories);
		const syncedAmounts: InvoiceAmount[] = this.syncLists(this.currentCostGroup().invoiceAmounts, invoiceAmounts);

		// Nur vollständige Lieferarten übernehmen (unfertige In-Table-Entwürfe verwerfen)
		const cleanedDeliveries = this.deliveries().filter((delivery) => delivery.commodity != null && delivery.physicalUnit != null);
		for (const delivery of cleanedDeliveries) {
			if (delivery.exportId?.length === 9) {
				delivery.exportId = undefined;
			}
		} //cleanup previous set IDs
		const costGroupRequest: CostGroupRequest = {
			exportId: this.currentCostGroup().exportId,
			costGroupTypeExportId: this.currentCostGroup().costGroupTypeExportId,
			name: this.costGroupFg.get('name')?.value ?? '',
			printname: this.costGroupFg.get('printName')?.value ?? '',
			description: this.costGroupFg.get('description')?.value ?? '',
			invoiceDeliveries: cleanedDeliveries,
			invoiceAmounts: syncedAmounts,
			invoiceCategories: syncedCategories,
			costAllocationExportId: this.costGroupFg.get('selectedAllocation')?.value?.exportId ?? '',
			bvedCostType: this.costGroupFg.get('selectedBvedCostType')?.value ?? null,
			costType: this.costGroupFg.get('selectedCostType')?.value ?? null,
			preferredVatRate: this.costGroupFg.get('preferredVatRate')?.value ?? null,
		};
		this.costGroupService
			.updateCostGroup(this.sessionService.effectiveArea, this.billingId, costGroupRequest.exportId, costGroupRequest)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (data) => {
					this.snackBar.success(this.transloco.translate('billing.updatedCostGroup'));
					this.dialogRef.close({ saved: true, data }); //close this dialog and the costGroupDialog
				},
				error: (err) => {
					this.snackBar.error(this.transloco.translate('errors.updatingBillingCostGroup'));
				},
			});
	}

	private syncLists(a: any[], b: any[]): any[] {
		const mapB = new Map(b.map((item) => [item.type, item]));

		//keep elements in A, if B has them also
		const kept = a.filter((item) => mapB.has(item.type));

		// add elements to A, if B has them
		const typesA = new Set(a.map((item) => item.type));
		const added = b.filter((item) => !typesA.has(item.type));

		return [...kept, ...added];
	}

	private fillInvoiceDeliveries() {
		this.deliveries.set(this.currentCostGroup().invoiceDeliveries);
	}

	editCommodity(delivery: InvoiceDelivery): void {
		this.isEditMode = true;
		this.editingCommodity = delivery;
		this.deliveryForm.get('commodity').setValue(delivery.commodity);
		this.deliveryForm.get('physicalUnit').setValue(delivery.physicalUnit);
	}

	addCommodity(delivery: InvoiceDelivery): void {
		this.deliveries.set([...this.deliveries(), delivery]);
	}

	updateCommodity(updatedCommodity: InvoiceDelivery): void {
		const index = this.deliveries().findIndex((c) => c.exportId === updatedCommodity.exportId);
		if (index > -1) {
			this.deliveries[index] = updatedCommodity;
			this.deliveries.set([...this.deliveries()]); // Trigger change detection
		}
	}

	deleteCommodity(delivery: InvoiceDelivery): void {
		this.deliveries.set(this.deliveries().filter((c) => c.exportId !== delivery.exportId));
	}

	saveCommodity(): void {
		if (this.deliveryForm.valid) {
			const formData = this.deliveryForm.value;

			if (this.isEditMode && this.editingCommodity) {
				// Update existing
				const updatedCommodity = {
					...this.editingCommodity,
					...formData,
				};
				this.updateCommodity(updatedCommodity);
			} else {
				// Add new
				const newCommodity = {
					id: this.generateId(), // setIds to identify edit
					...formData,
				};
				this.addCommodity(newCommodity);
			}

			this.resetForm();
		}
	}

	private resetForm(): void {
		this.deliveryForm.reset();
		this.isEditMode = false;
		this.editingCommodity = null;
	}

	cancelEdit(): void {
		this.resetForm();
	}

	private generateId(): string {
		return Math.random().toString(36).substr(2, 9);
	}

	protected getAllocationName() {
		return this.costAllocationDetailService.getAllocationDisplayName(this.costGroupFg.get('selectedAllocation')?.value);
	}

	/**
	 * Holt den bved-Key für einen BvedCostType
	 * @param enumName Der BvedCostType Enum-Name
	 * @returns Der Key im Format "x.y.z" (z.B. "6.2.16")
	 */
	protected getBvedCostTypeKey(enumName: string): string {
		return BvedCostTypeData[enumName]?.key || '';
	}

	/**
	 * Holt die Hauptkategorie für einen BvedCostType
	 * @param enumName Der BvedCostType Enum-Name
	 * @returns Die Hauptkategorie (1-17) oder null
	 */
	protected getBvedMainCategory(enumName: string): BvedMainCategory | null {
		const key = this.getBvedCostTypeKey(enumName);
		return getMainCategoryFromKey(key);
	}

	/**
	 * Gibt den i18n Key für die Hauptkategorie zurück
	 * @param mainCategory Die Hauptkategorie
	 * @returns Der i18n Key für die Übersetzung
	 */
	protected getBvedMainCategoryI18nKey(mainCategory: BvedMainCategory | null): string {
		return getMainCategoryI18nKey(mainCategory);
	}
}
